import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaPlus,
  FaExpand,
  FaCog,
  FaTrash,
  FaEdit,
  FaSync,
  FaArrowsAlt,
  FaCompress,
  FaArrowLeft,
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import WidgetBuilder from './WidgetBuilder';
import ChartDisplay from '../reports/charts/ChartDisplay';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import useReportChartData from '../../hooks/useReportChartData';
import { transformDatasetToPreviewData } from '../../utils/reportChartData';
import { formatDateTime } from '../../utils/formatUtils';
import { resolveDashboardRunUserDisplayLabel } from '../../utils/dashboardRunUserLabel';
import ReportViewer from '../reports/ReportViewer';
import './css/DashboardCanvas.css';

const DashboardCanvas = ({ isRunMode = false, dashboardId: propDashboardId, showHeader = true, embedded = false }) => {
  const { dashboardId: urlDashboardId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dashboardId = propDashboardId || urlDashboardId;
  const viewAsUserIdFromUrl = searchParams.get('viewAsUserId') || undefined;
  const [widgets, setWidgets] = useState([]);
  const [originalWidgets, setOriginalWidgets] = useState([]); // Track original widgets for deletion detection
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const [widgetChartData, setWidgetChartData] = useState({});
  const [loadingWidgets, setLoadingWidgets] = useState({});
  const [saving, setSaving] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [runUserDisplayLabel, setRunUserDisplayLabel] = useState('');
  const [viewingWidget, setViewingWidget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [widgetToDelete, setWidgetToDelete] = useState(null);

  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [resizePreview, setResizePreview] = useState(null);
  const [draggedWidget, setDraggedWidget] = useState(null); // Still needed for resize functionality
  const [resizeStartPosition, setResizeStartPosition] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);
  const dashboardRunUserIdRef = useRef(null);
  const { loadReportChartData } = useReportChartData();
  console.log('DashboardCanvas - isRunMode:', isRunMode);
  console.log('DashboardCanvas - dashboardId:', dashboardId);
  // Load dashboard details and widgets together
  const loadDashboardAndWidgets = useCallback(async () => {
    try {
      if (!dashboardId) {
        console.error('DashboardCanvas - No dashboardId provided');
        setError('No dashboard ID provided');
        setLoading(false);
        return;
      }

      const dashboardApi = new DataApi('dashboard');
      const viewAsQs =
        viewAsUserIdFromUrl != null && viewAsUserIdFromUrl !== ''
          ? `?viewAsUserId=${encodeURIComponent(viewAsUserIdFromUrl)}`
          : '';
      const response = await dashboardApi.get(
        `/${dashboardId}/widgets${viewAsQs}`
      );
      console.log('DashboardCanvas - Response:', response);
      if (response.data.success) {
        const dashboardWithWidgets = response.data.data;
        console.log('DashboardCanvas - Loaded dashboard with widgets:', dashboardWithWidgets);

        // Extract dashboard details
        const { widgets: widgetsData, ...dashboardDetails } = dashboardWithWidgets;
        dashboardRunUserIdRef.current = dashboardDetails.run_user_id || null;
        setDashboard(dashboardDetails);
        console.log('DashboardCanvas - Dashboard details:', dashboardDetails);
        console.log('DashboardCanvas - Dashboard name:', dashboardDetails.name);
        console.log('DashboardCanvas - Dashboard label:', dashboardDetails.label);
        console.log('DashboardCanvas - Dashboard description:', dashboardDetails.description);

        // Filter out empty or invalid widgets
        const validWidgets = widgetsData.filter(widget => {
          // Check if widget exists and has required fields
          if (!widget || !widget.id) return false;

          // Ensure widget has a meaningful name
          if (!widget.name || widget.name.trim() === '') return false;

          const widgetType = (widget.type || widget.widget_type || '').toLowerCase();
          const parentId = widget.parent_id || widget.report_id || null;

          // For chart/report widgets we require a parent report id
          if ((widgetType === 'chart' || widgetType === 'report' || !widgetType) && !parentId) {
            return false;
          }

          // Additional checks for common invalid patterns
          if (widget.name.toLowerCase().includes('test') && !parentId) return false;
          if (widget.name.toLowerCase().includes('default') && !parentId) return false;
          if (widget.name.toLowerCase().includes('sample') && !parentId) return false;

          return true;
        });

        console.log('DashboardCanvas - Valid widgets after filtering:', validWidgets);
        setWidgets(validWidgets);
        setOriginalWidgets(validWidgets); // Store original widgets to detect deletions

        // Load chart data for each valid widget
        if (validWidgets.length > 0) {
          loadWidgetChartData(validWidgets);
        }
      } else {
        setError('Failed to load dashboard and widgets');
      }
    } catch (err) {
      console.error('Error loading dashboard and widgets:', err);
      setError('Error loading dashboard and widgets. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [dashboardId, viewAsUserIdFromUrl]);

  useEffect(() => {
    if (dashboardId) {
      loadDashboardAndWidgets();
    }
  }, [dashboardId, viewAsUserIdFromUrl, loadDashboardAndWidgets]);

  useEffect(() => {
    let cancelled = false;
    const runUserId = dashboard?.run_user_id;
    if (runUserId == null || String(runUserId).trim() === '') {
      setRunUserDisplayLabel('');
      return undefined;
    }
    (async () => {
      const label = await resolveDashboardRunUserDisplayLabel(runUserId);
      if (!cancelled) {
        setRunUserDisplayLabel(label);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dashboard?.run_user_id]);

  const loadWidgetChartData = async (widgetsData) => {
    console.log('DashboardCanvas - Loading chart data for widgets:', widgetsData);
    for (const widget of widgetsData) {
      const normalizedChartType = (widget.chart_type || widget.type || '').toLowerCase();
      if (normalizedChartType === 'report' || normalizedChartType === 'dashboard') {
        console.log('DashboardCanvas - Skipping chart data load for widget:', widget.id, 'type:', normalizedChartType);
        continue;
      }
      const parentId = widget.parent_id || widget.report_id || null;
      console.log('DashboardCanvas - Processing widget:', {
        id: widget.id,
        name: widget.name,
        parent_id: parentId,
        chart_type: widget.chart_type || (widget.chart_config && widget.chart_config.chartType),
        hasSavedDataset: !!widget.dataset,
        hasChartConfig: !!widget.chart_config
      });

      const shouldRefreshForLoggedInUserMismatch = (() => {
        if ((dashboard?.view_type || '').toUpperCase() !== 'LOGGED_IN_USER') {
          return false;
        }
        const currentRunUserId = dashboardRunUserIdRef.current || null;
        const savedRunUserId = widget?.dataset?._metadata?.run_user_id || null;
        if (!currentRunUserId) {
          return false;
        }
        // For logged-in-user dashboards, reuse cache only when the dataset was saved by same run user.
        return !savedRunUserId || savedRunUserId !== currentRunUserId;
      })();

      const shouldRefreshRecordCountArcChart = (() => {
        const cfg = widget.chart_config || {};
        const type = String(cfg.chartType || widget.chart_type || '').toLowerCase();
        const valueField = String(cfg.pieValueField || '').toLowerCase();
        return (type === 'pie' || type === 'donut') && valueField === 'record_count';
      })();

      if (shouldRefreshForLoggedInUserMismatch) {
        console.log(
          'DashboardCanvas - Logged-in user changed since last saved dataset, refreshing widget:',
          widget.id
        );
        await loadChartDataForWidget(widget);
        continue;
      }

      if (shouldRefreshRecordCountArcChart) {
        console.log(
          'DashboardCanvas - Record-count pie/donut detected, bypassing cached dataset for widget:',
          widget.id
        );
        await loadChartDataForWidget(widget);
        continue;
      }

      // Check if widget has saved dataset with actual content
      if (widget.dataset && Object.keys(widget.dataset).length > 0) {
        console.log('DashboardCanvas - Using cached dataset for widget:', widget.id);
        console.log('DashboardCanvas - Dataset structure:', widget.dataset);

        // Check if the cached dataset has the proper structure for ChartDisplay
        if (isDatasetStructureValid(widget.dataset)) {
          console.log('DashboardCanvas - Cached dataset structure is valid, using cached data');
          await processCachedDataset(widget);
        } else {
          console.log('DashboardCanvas - Cached dataset structure is invalid, loading fresh data from API');
          await loadChartDataForWidget(widget);
        }
      } else if (widget.dataset && Object.keys(widget.dataset).length === 0) {
        console.log('DashboardCanvas - Widget has empty dataset, loading from API for widget:', widget.id);
        await loadChartDataForWidget(widget);
      } else if (parentId) {
        console.log('DashboardCanvas - No saved dataset, loading from API for widget:', widget.id);
        await loadChartDataForWidget(widget);
      } else {
        console.log('DashboardCanvas - Widget has no parent/report, skipping:', widget.id);
      }
    }
  };

  // Helper function to check if cached dataset has valid structure for ChartDisplay
  const isDatasetStructureValid = (dataset) => {
    if (!dataset || !dataset._metadata) {
      console.log('DashboardCanvas - Dataset missing _metadata');
      return false;
    }

    const metadata = dataset._metadata;

    // Check if it has the required metadata structure
    if (!metadata.group_fields || !metadata.summarized_fields || !metadata.report_info) {
      console.log('DashboardCanvas - Dataset missing required metadata fields');
      return false;
    }

    // Check if it has the proper data structure that ChartDisplay expects
    const groupFields = Object.keys(metadata.group_fields);
    const summarizedFields = Object.keys(metadata.summarized_fields);

    if (groupFields.length === 0) {
      console.log('DashboardCanvas - Dataset has no group fields');
      return false;
    }

    // Check if the main data structure exists (e.g., "name" field with group values)
    const hasValidDataStructure = groupFields.some(groupField => {
      return dataset[groupField] && typeof dataset[groupField] === 'object';
    });

    if (!hasValidDataStructure) {
      console.log('DashboardCanvas - Dataset missing valid data structure for group fields');
      return false;
    }

    console.log('DashboardCanvas - Dataset structure is valid');
    return true;
  };

  // Helper function to process cached dataset and set up widget chart data
  const processCachedDataset = async (widget) => {
    try {
      console.log('DashboardCanvas - Processing cached dataset for widget:', widget.id);

      // Extract metadata and fields from the cached dataset
      const metadata = widget.dataset._metadata || {};
      const groupFields = metadata.group_fields || {};
      const summarizedFields = metadata.summarized_fields || {};

      // Transform dataset to previewData array format (same as before)
      const previewData = transformDatasetToPreviewData(widget.dataset);
      console.log('DashboardCanvas - Transformed previewData from cached dataset:', previewData);

      // Create field arrays in the format expected by ChartDisplay
      const selectedFields = Object.keys(summarizedFields).map(fieldName => {
        const fieldInfo = summarizedFields[fieldName];
        return {
          id: fieldInfo.field_id,
          field_name: fieldName,
          field_label: fieldInfo.field_label,
          data_type: fieldInfo.data_type,
          object_name: fieldInfo.object_name,
          is_aggregated: true,
          aggregate_function: fieldInfo.aggregation,
          is_grouped: false,
          sort_order: 1,
          summaryType: fieldInfo.aggregation
        };
      });

      // Always add record_count field if it exists in the cached data
      if (summarizedFields['record_count']) {
        selectedFields.push({
          id: 'record_count',
          field_name: 'record_count',
          field_label: 'Record Count',
          data_type: 'number',
          object_name: 'virtual',
          is_aggregated: true,
          aggregate_function: 'count',
          is_grouped: false,
          sort_order: 1,
          summaryType: 'count'
        });
      }

      const selectedGroups = Object.keys(groupFields).map(fieldName => {
        const fieldInfo = groupFields[fieldName];
        return {
          id: fieldInfo.field_id,
          field_name: fieldName,
          field_label: fieldInfo.field_label,
          data_type: fieldInfo.data_type,
          object_name: fieldInfo.object_name,
          is_aggregated: false,
          aggregate_function: null,
          is_grouped: true,
          sort_order: 1,
          summaryType: 'none'
        };
      });

      // Create summarizedFields object in the format expected by ChartDisplay
      const processedSummarizedFields = {};
      Object.keys(summarizedFields).forEach(fieldName => {
        const fieldInfo = summarizedFields[fieldName];
        processedSummarizedFields[fieldInfo.field_id] = {
          aggregation: fieldInfo.aggregation
        };
      });

      // Also check if the chart configuration has summarizedFields (for record_count)
      if (widget.chart_config && widget.chart_config.summarizedFields) {
        console.log('DashboardCanvas - Found summarizedFields in chart_config:', widget.chart_config.summarizedFields);
        Object.keys(widget.chart_config.summarizedFields).forEach(fieldName => {
          const fieldInfo = widget.chart_config.summarizedFields[fieldName];
          // Use field_name as the key if field_id is not available (for virtual fields like record_count)
          const key = fieldInfo.field_id || fieldName;
          processedSummarizedFields[key] = {
            aggregation: fieldInfo.aggregation
          };
        });
      }

      // Set up widget chart data with cached dataset
      setWidgetChartData(prev => ({
        ...prev,
        [widget.id]: {
          previewData: previewData,
          summarizedData: widget.dataset,
          groupingInfo: {
            hasGrouping: true,
            groupedFields: selectedGroups,
            nonGroupedFields: selectedFields
          },
          selectedFields: selectedFields,
          summarizedFields: processedSummarizedFields,
          selectedGroups: selectedGroups
        }
      }));

      // Update the widget in state
      setWidgets(prev => prev.map(w =>
        w.id === widget.id ? { ...w, summarizedData: widget.dataset, dataset: widget.dataset } : w
      ));

      console.log('DashboardCanvas - Successfully processed cached dataset for widget:', widget.id);
    } catch (err) {
      console.error('DashboardCanvas - Error processing cached dataset:', err);
      // Fallback to loading fresh data if cached processing fails
      await loadChartDataForWidget(widget);
    }
  };

  // Function to update dashboard widget in the backend
  const updateDashboardWidgetInBackend = async (widgetId, updatedData) => {
    try {
      console.log('DashboardCanvas - Updating dashboard widget in backend:', widgetId, updatedData);

      const dashboardApi = new DataApi('dashboard');
      const response = await dashboardApi.put(`/widgets/${widgetId}`, updatedData);

      if (response?.data?.success) {
        console.log('DashboardCanvas - Successfully updated dashboard widget in backend:', widgetId);
        return response.data.data;
      } else {
        console.error('DashboardCanvas - Failed to update dashboard widget in backend:', response?.data?.message);
        throw new Error(response?.data?.message || 'Failed to update dashboard widget');
      }
    } catch (error) {
      console.error('DashboardCanvas - Error updating dashboard widget in backend:', error);
      // Don't throw the error to prevent breaking the main flow
      // The widget will still work with the updated data in memory
    }
  };

  const loadChartDataForWidget = async (widget, options = {}) => {
    if (!widget) {
      console.warn('DashboardCanvas - Cannot load chart data, widget is undefined');
      return;
    }

    const parentId = widget.parent_id || widget.report_id;

    if (!parentId) {
      console.warn('DashboardCanvas - Cannot load chart data, missing parent/report ID for widget:', widget?.id);
      return;
    }

    try {
      console.log('DashboardCanvas - Loading chart data for widget:', widget.id);
      setLoadingWidgets(prev => ({ ...prev, [widget.id]: true }));

      const result = await loadReportChartData({
        reportId: parentId,
        chartId: widget.chart_config?.id || widget.chart_id,
        existingChartConfig: widget.chart_config,
        limit: 100000,
        runningUserId: dashboardRunUserIdRef.current || undefined,
        dashboardId: dashboardId || undefined,
        viewAsUserId: viewAsUserIdFromUrl,
        ...options,
      });

      const dataset = result.dataset || {};
      const datasetWithRunUser = {
        ...dataset,
        _metadata: {
          ...(dataset?._metadata || {}),
          run_user_id: dashboardRunUserIdRef.current || null,
        },
      };
      const previewData = result.previewData || result.rawData || [];
      const chartConfig = result.chartConfig || widget.chart_config;

      setWidgetChartData(prev => ({
        ...prev,
        [widget.id]: {
          previewData,
          summarizedData: datasetWithRunUser,
          groupingInfo: result.groupingInfo,
          selectedFields: result.selectedFields || [],
          summarizedFields: result.summarizedFields || {},
          selectedGroups: result.selectedGroups || []
        }
      }));

      setWidgets(prev => prev.map(w =>
        w.id === widget.id
          ? {
            ...w,
            dataset: datasetWithRunUser,
            summarizedData: datasetWithRunUser,
            selectedFields: result.selectedFields || [],
            summarizedFields: result.summarizedFields || {},
            selectedGroups: result.selectedGroups || [],
            chart_config: chartConfig,
            refresh_datetime: new Date().toISOString()
          }
          : w
      ));

      await updateDashboardWidgetInBackend(widget.id, {
        chart_config: chartConfig,
        dataset: datasetWithRunUser,
        refresh_datetime: new Date().toISOString()
      });
    } catch (err) {
      console.error(`Error loading chart data for widget ${widget?.id}:`, err);
    } finally {
      setLoadingWidgets(prev => ({ ...prev, [widget?.id]: false }));
    }
  };

  const handleDeleteWidget = (widget) => {
    setWidgetToDelete(widget);
    setShowDeleteModal(true);
  };

  const confirmDeleteWidget = async () => {
    if (!widgetToDelete) return;

    try {
      const dashboardApi = new DataApi('dashboard');
      const response = await dashboardApi.delete(`/widgets/${widgetToDelete.id}`);

      if (response.data.success) {
        loadDashboardAndWidgets(); // Reload the list
      } else {
        setError('Failed to delete widget');
      }
    } catch (err) {
      console.error('Error deleting widget:', err);
      setError('Error deleting widget. Please try again.');
    } finally {
      setWidgetToDelete(null);
    }
  };


  const handleAddWidget = async (newWidget) => {
    console.log('DashboardCanvas - Received widget:', newWidget);
    console.log('DashboardCanvas - Widget column_span:', newWidget.column_span);

    // Set default column span to 6 (half of 12-column grid)
    const widgetWithDefaults = {
      ...newWidget,
      column_span: newWidget.column_span || 6,
      row_span: newWidget.row_span || 4
    };

    console.log('DashboardCanvas - Final widget column_span:', widgetWithDefaults.column_span);
    console.log('DashboardCanvas - Final widget data:', widgetWithDefaults);

    // Add the new widget to the list (now it's a preview)
    setWidgets(prev => [...prev, widgetWithDefaults]);
    setShowSidebar(false);

    const widgetType = (widgetWithDefaults.type || widgetWithDefaults.widget_type || '').toLowerCase();
    const parentId = widgetWithDefaults.parent_id || widgetWithDefaults.report_id;

    if (widgetWithDefaults.isPreview && widgetType === 'report') {
      setWidgetChartData(prev => {
        const updated = { ...prev };
        delete updated[widgetWithDefaults.id];
        return updated;
      });
    } else if (parentId && widgetType === 'chart') {
      await loadChartDataForWidget(widgetWithDefaults);
    }
  };

  // Resize handlers
  const handleMouseDown = (e, widget, action) => {
    e.preventDefault();
    e.stopPropagation();

    if (action === 'resize') {
      // Get the handle from the current target (the resize handle div)
      const handle = e.currentTarget.dataset.handle || 'bottom-right';

      console.log('DashboardCanvas - Resize started:', {
        widgetId: widget.id,
        handle: handle,
        currentColumnSpan: widget.column_span,
        currentRowSpan: widget.row_span
      });
      setIsResizing(true);
      setResizeHandle(handle);
      setDraggedWidget(widget);
      setResizeStartPosition({ x: e.clientX, y: e.clientY });

      // Add visual feedback
      document.body.style.cursor = getResizeCursor(handle);
      document.body.style.userSelect = 'none';

      // Add event listeners immediately
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
  };

  const handleMouseMove = useCallback((e) => {
    // Debug: Log mouse move events during resize
    if (isResizing) {
      console.log('DashboardCanvas - Mouse move during resize:', {
        isResizing: isResizing,
        draggedWidget: draggedWidget?.id,
        resizeHandle: resizeHandle
      });
    }

    if (isResizing && draggedWidget && resizeHandle) {
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      if (!canvasRect) return;

      const canvasWidth = canvasRect.width;
      const gridCellWidth = (canvasWidth - (11 * 20)) / 12;
      const gridRowHeight = 120; // Fixed row height - matches CSS grid-auto-rows

      let newColumnSpan = draggedWidget.column_span;
      let newRowSpan = draggedWidget.row_span;

      console.log('DashboardCanvas - Resize in progress:', {
        widgetId: draggedWidget.id,
        handle: resizeHandle,
        mouseX: e.clientX,
        mouseY: e.clientY,
        canvasRect: canvasRect,
        gridCellWidth: gridCellWidth,
        currentColumnSpan: draggedWidget.column_span,
        currentRowSpan: draggedWidget.row_span
      });

      if (resizeHandle.includes('right')) {
        const currentX = draggedWidget.x || 0;
        const maxColumns = 12 - currentX;
        const mouseX = e.clientX - canvasRect.left;
        const widgetLeftX = currentX * (gridCellWidth + 20); // Include gap between columns
        const relativeX = mouseX - widgetLeftX;

        // Snap to grid with minimum 1 column
        newColumnSpan = Math.max(1, Math.min(maxColumns, Math.round(relativeX / (gridCellWidth + 20))));

        // Ensure we don't exceed grid boundaries
        if (currentX + newColumnSpan > 12) {
          newColumnSpan = 12 - currentX;
        }
      }

      if (resizeHandle.includes('bottom')) {
        // Calculate the change in mouse position from the start
        const deltaY = e.clientY - resizeStartPosition.y;
        const rowHeight = gridRowHeight + 20; // Include gap between rows

        // Calculate how many rows to add/subtract based on mouse movement
        const rowChange = Math.round(deltaY / rowHeight);

        // Apply the change to the original row span
        const originalRowSpan = draggedWidget.row_span || 4;
        newRowSpan = Math.max(1, Math.min(originalRowSpan + rowChange, 6));

        console.log('DashboardCanvas - Bottom resize calculation:', {
          deltaY,
          rowHeight,
          rowChange,
          originalRowSpan,
          finalRowSpan: newRowSpan
        });
      }

      // Update resize preview
      setResizePreview({ column_span: newColumnSpan, row_span: newRowSpan });

      console.log('DashboardCanvas - Resize calculation:', {
        widgetId: draggedWidget.id,
        handle: resizeHandle,
        newColumnSpan: newColumnSpan,
        newRowSpan: newRowSpan,
        oldColumnSpan: draggedWidget.column_span,
        oldRowSpan: draggedWidget.row_span
      });

      setWidgets(prev => prev.map(widget =>
        widget.id === draggedWidget.id
          ? { ...widget, column_span: newColumnSpan, row_span: newRowSpan }
          : widget
      ));
    }
  }, [isResizing, draggedWidget, resizeHandle, resizeStartPosition]);

  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      console.log('DashboardCanvas - Resize ended:', {
        widgetId: draggedWidget?.id,
        finalColumnSpan: draggedWidget?.column_span,
        finalRowSpan: draggedWidget?.row_span
      });
    }

    // Remove event listeners
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);

    setIsResizing(false);
    setDraggedWidget(null);
    setResizeHandle(null);
    setResizePreview(null);
    setResizeStartPosition({ x: 0, y: 0 });

    // Reset cursor and user selection
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [isResizing, draggedWidget]);

  // Helper function to get appropriate resize cursor
  const getResizeCursor = (handle) => {
    switch (handle) {
      case 'bottom-right':
        return 'se-resize';
      case 'bottom':
        return 's-resize';
      case 'right':
        return 'e-resize';
      default:
        return 'default';
    }
  };

  // Add event listeners for resize
  useEffect(() => {
    if (isResizing) {
      console.log('DashboardCanvas - Adding mouse event listeners');
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        console.log('DashboardCanvas - Removing mouse event listeners');
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);

  const handlePreviewWidget = (widgetConfig) => {
    // Handle widget preview/update
    console.log('DashboardCanvas - Adding/updating widget:', widgetConfig);
    console.log('DashboardCanvas - Chart configuration received:', {
      selectedFields: widgetConfig.selectedFields,
      summarizedFields: widgetConfig.summarizedFields,
      selectedGroups: widgetConfig.selectedGroups,
      previewData: widgetConfig.previewData,
      summarizedData: widgetConfig.summarizedData,
      selectedChart: widgetConfig.selectedChart,
      isPreview: widgetConfig.isPreview,
      id: widgetConfig.id
    });

    console.log('DashboardCanvas - Widget config details:', {
      hasPreviewData: !!widgetConfig.previewData,
      hasSummarizedData: !!widgetConfig.summarizedData,
      hasGroupingInfo: !!widgetConfig.groupingInfo,
      previewDataType: typeof widgetConfig.previewData,
      summarizedDataType: typeof widgetConfig.summarizedData,
      groupingInfoType: typeof widgetConfig.groupingInfo
    });

    const widgetTypeValue = (widgetConfig.type || widgetConfig.chart_type || '').toLowerCase();

    if (widgetConfig.isPreview) {
      // Add new widget to the widgets state
      setWidgets(prev => [...prev, widgetConfig]);
    } else {
      // Update existing widget in the widgets state
      setWidgets(prev => prev.map(widget => {
        if (widget.id === widgetConfig.id) {
          // Preserve existing important properties if widgetConfig doesn't have them
          const preservedSummarizedData = widgetConfig.summarizedData || widget.summarizedData;
          const preservedDataset = widgetConfig.dataset || widget.dataset;
          const preservedChartId = widgetConfig.chart_id || widget.chart_id;
          const preservedChartConfig = widgetConfig.chart_config || widget.selectedChart || widget.chart_config;
          const preservedParentId = widgetConfig.parent_id || widget.parent_id || widget.report_id || null;
          const normalizedType = widgetTypeValue || (widget.type || '').toLowerCase();

          console.log('DashboardCanvas - Updating widget with preserved data:', {
            widgetId: widget.id,
            widgetName: widget.name,
            hasOriginalSummarizedData: !!widget.summarizedData,
            hasOriginalDataset: !!widget.dataset,
            hasOriginalChartId: !!widget.chart_id,
            hasOriginalChartConfig: !!widget.chart_config,
            hasConfigSummarizedData: !!widgetConfig.summarizedData,
            hasConfigDataset: !!widgetConfig.dataset,
            hasConfigChartId: !!widgetConfig.chart_id,
            hasConfigChartConfig: !!widgetConfig.selectedChart,
            hasPreservedSummarizedData: !!preservedSummarizedData,
            hasPreservedDataset: !!preservedDataset,
            hasPreservedChartId: !!preservedChartId,
            hasPreservedChartConfig: !!preservedChartConfig,
            preservedParentId
          });

          return {
            ...widgetConfig,
            summarizedData: normalizedType === 'report' ? null : preservedSummarizedData,
            dataset: normalizedType === 'report' ? null : preservedDataset,
            chart_id: normalizedType === 'report' ? null : preservedChartId,
            chart_config: (normalizedType === 'report' || normalizedType === 'dashboard') ? null : preservedChartConfig,
            parent_id: preservedParentId
          };
        }
        return widget;
      }));
    }

    if ((widgetConfig.chart_type || widgetConfig.type) === 'report' || (widgetConfig.chart_type || widgetConfig.type) === 'dashboard') {
      setWidgetChartData(prev => {
        const updated = { ...prev };
        if (widgetConfig.id) {
          delete updated[widgetConfig.id];
        }
        return updated;
      });
    } else if (widgetConfig.previewData && widgetConfig.summarizedData && widgetConfig.groupingInfo) {
      console.log('DashboardCanvas - Using existing chart data for widget:', widgetConfig.id);
      setWidgetChartData(prev => ({
        ...prev,
        [widgetConfig.id]: {
          previewData: widgetConfig.previewData,
          summarizedData: widgetConfig.summarizedData,
          groupingInfo: widgetConfig.groupingInfo,
          // Include chart configuration
          selectedFields: widgetConfig.selectedFields || [],
          summarizedFields: widgetConfig.summarizedFields || {},
          selectedGroups: widgetConfig.selectedGroups || []
        }
      }));
    } else {
      // Check if we already have chart data for this widget (preserve existing data)
      const existingChartData = widgetChartData[widgetConfig.id];
      if (existingChartData) {
        console.log('DashboardCanvas - Preserving existing chart data for updated widget:', widgetConfig.id);
        // Keep the existing chart data, just update the widget configuration
        setWidgetChartData(prev => ({
          ...prev,
          [widgetConfig.id]: {
            ...existingChartData,
            // Update any new configuration if provided
            selectedFields: widgetConfig.selectedFields || existingChartData.selectedFields,
            summarizedFields: widgetConfig.summarizedFields || existingChartData.summarizedFields,
            selectedGroups: widgetConfig.selectedGroups || existingChartData.selectedGroups
          }
        }));
      } else if (widgetTypeValue !== 'dashboard' && widgetConfig.dataset) {
        // If widget has dataset from database, use it directly and standardize to summarizedData
        console.log('DashboardCanvas - Using dataset from database for updated widget:', widgetConfig.id);
        const metadata = widgetConfig.dataset._metadata || {};
        const groupFields = metadata.group_fields || {};
        const summarizedFields = metadata.summarized_fields || {};

        // Transform dataset to previewData array format
        const previewData = transformDatasetToPreviewData(widgetConfig.dataset);
        console.log('DashboardCanvas - Transformed previewData for updated widget:', previewData);

        // Use the transformed data
        setWidgetChartData(prev => ({
          ...prev,
          [widgetConfig.id]: {
            previewData: previewData,
            summarizedData: widgetConfig.dataset,
            groupingInfo: null,
            selectedFields: Object.keys(summarizedFields),
            summarizedFields: summarizedFields,
            selectedGroups: Object.keys(groupFields)
          }
        }));

        // Update the widget to have consistent summarizedData while preserving original dataset
        setWidgets(prev => prev.map(w =>
          w.id === widgetConfig.id ? { ...w, summarizedData: widgetConfig.dataset, dataset: widgetConfig.dataset } : w
        ));
      } else {
        // Load chart data for the widget from API only if no existing data
        console.log('DashboardCanvas - Loading chart data from API for widget:', widgetConfig.id);
        console.log('DashboardCanvas - Widget config for API load:', {
          id: widgetConfig.id,
          name: widgetConfig.name,
          parent_id: widgetConfig.parent_id,
          chart_type: widgetConfig.chart_type,
          hasPreviewData: !!widgetConfig.previewData,
          hasSummarizedData: !!widgetConfig.summarizedData,
          hasGroupingInfo: !!widgetConfig.groupingInfo
        });

        // Ensure we load chart data for the updated widget
        loadChartDataForWidget(widgetConfig);
      }
    }
  };

  const handleRemoveWidget = (widgetId) => {
    console.log('DashboardCanvas - Removing widget:', widgetId);

    // Remove widget from UI state
    setWidgets(prev => prev.filter(widget => widget.id !== widgetId));

    // Remove chart data for the widget
    setWidgetChartData(prev => {
      const newData = { ...prev };
      delete newData[widgetId];
      return newData;
    });
  };

  const handleEditWidget = (widget) => {
    console.log('DashboardCanvas - Editing widget:', widget);

    // Open WidgetBuilder in edit mode
    setShowSidebar(true);
    setEditingWidget(widget);
  };

  const handleViewWidget = (widget) => {
    console.log('DashboardCanvas - Viewing widget:', widget);
    setViewingWidget(widget);
  };

  const handleCloseViewModal = () => {
    setViewingWidget(null);
  };

  // Refresh widget data from API
  const handleRefreshWidget = async (widget) => {
    const normalizedChartType = (widget.chart_type || widget.type || '').toLowerCase();
    if (normalizedChartType === 'report') {
      console.log('DashboardCanvas - Refresh skipped for full report widget:', widget.id);
      return;
    }
    try {
      console.log('DashboardCanvas - Refreshing widget data:', widget.id);
      setLoadingWidgets(prev => ({ ...prev, [widget.id]: true }));

      // Load fresh data from API
      await loadChartDataForWidget(widget);

      console.log('DashboardCanvas - Widget refreshed successfully:', widget.id);
    } catch (err) {
      console.error('Error refreshing widget:', err);
    } finally {
      setLoadingWidgets(prev => ({ ...prev, [widget.id]: false }));
    }
  };

  const handleSaveDashboard = async () => {
    // Prevent multiple simultaneous saves
    if (saving) {
      console.log('DashboardCanvas - Save already in progress, ignoring duplicate save request');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Detect deleted widgets (widgets that were in originalWidgets but not in current widgets)
      const currentWidgetIds = widgets.map(w => w.id).filter(id => id && !id.toString().startsWith('preview-'));
      const deletedWidgets = originalWidgets.filter(original =>
        original.id && !currentWidgetIds.includes(original.id)
      );

      console.log('DashboardCanvas - Widget deletion analysis:', {
        originalCount: originalWidgets.length,
        currentCount: widgets.length,
        deletedCount: deletedWidgets.length,
        deletedWidgets: deletedWidgets.map(w => ({ id: w.id, name: w.name }))
      });

      // Send ALL widgets (from database + newly created from WidgetBuilder)
      // Backend will handle CREATE vs UPDATE based on ID presence
      console.log('DashboardCanvas - Sending all widgets for upsert:', {
        total: widgets.length,
        new: widgets.filter(w => !w.id || w.id.toString().startsWith('preview-')).length,
        existing: widgets.filter(w => w.id && !w.id.toString().startsWith('preview-')).length,
        deleted: deletedWidgets.length
      });

      if (widgets.length === 0) {
        setError('No widgets to save');
        setSaving(false);
        return;
      }

      const dashboardApi = new DataApi('dashboard');

      // Prepare all widgets data for upsert
      const widgetsData = widgets.map(widget => {

        // Use summarizedData (now standardized), but handle empty dataset objects
        let dataset = widget.summarizedData || null;

        // If summarizedData is null but widget has dataset, use it (even if empty)
        if (!dataset && widget.dataset !== undefined) {
          dataset = widget.dataset;
        }

        // Blank ID for new widgets (preview-xxx IDs), keep ID for existing widgets
        const widgetId = (!widget.id || widget.id.toString().startsWith('preview-')) ? null : widget.id;

        console.log('DashboardCanvas - Widget dataset sources:', {
          widgetId: widgetId,
          widgetName: widget.name,
          hasSummarizedData: !!widget.summarizedData,
          hasDataset: widget.dataset !== undefined,
          datasetIsEmpty: widget.dataset && Object.keys(widget.dataset).length === 0,
          finalDataset: !!dataset,
          finalDatasetType: typeof dataset,
          finalDatasetKeys: dataset && typeof dataset === 'object' ? Object.keys(dataset) : 'N/A'
        });

        const normalizedChartType = (widget.chart_type || widget.type || (widget.chart_config && widget.chart_config.chartType) || '').toLowerCase();
        const chartTypeValue = widget.chart_type || (widget.chart_config && widget.chart_config.chartType) || 'chart';
        const chartConfigValue = normalizedChartType === 'report' ? null : (widget.chart_config || widget.selectedChart);
        const chartIdValue = normalizedChartType === 'report' ? null : widget.chart_id;
        const datasetValue = normalizedChartType === 'report' ? null : dataset;
        const parentIdValue = widget.parent_id || widget.report_id || null;

        console.log('DashboardCanvas - Preparing widget for upsert:', {
          id: widgetId, // null for CREATE, actual ID for UPDATE
          name: widget.name,
          parent_id: parentIdValue,
          chart_id: chartIdValue,
          chart_type: chartTypeValue,
          chart_config: chartConfigValue,
          column_span: widget.column_span,
          row_span: widget.row_span,
          sort_order: widget.sort_order || widgets.indexOf(widget),
          dataset: datasetValue
        });

        const widgetTypeValue = normalizedChartType === 'report' ? 'report' : (normalizedChartType === 'dashboard' ? 'dashboard' : 'chart');

        return {
          id: widgetId, // null for CREATE, actual ID for UPDATE
          name: widget.name,
          type: widgetTypeValue,
          parent_id: parentIdValue,
          chart_id: chartIdValue,
          chart_config: chartConfigValue,
          column_span: widget.column_span,
          row_span: widget.row_span,
          sort_order: widget.sort_order || widgets.indexOf(widget),
          dataset: datasetValue,
          refresh_datetime: widget.refresh_datetime || null
        };
      });

      console.log('DashboardCanvas - Sending widgets for upsert:', {
        count: widgetsData.length,
        new: widgetsData.filter(w => w.id === null).length,
        existing: widgetsData.filter(w => w.id !== null).length
      });

      /*let executesave  = false;
      const response = null;
      if(executesave === true){
        // Send all widgets in single batch - backend handles upsert logic
        response = await dashboardApi.post(`/${dashboardId}/widgets/upsert`, {
          widgets: widgetsData
        });
      }*/

      const response = await dashboardApi.post(`/${dashboardId}/widgets/upsert`, {
        widgets: widgetsData,
        deletedWidgets: deletedWidgets.map(w => ({ id: w.id, name: w.name })) // Send deleted widget IDs
      });

      if (!response.data.success) {
        throw new Error(`Failed to save widgets: ${response.data.message || 'Unknown error'}`);
      }

      // Update widgets with their final IDs
      const savedWidgets = response.data.data;
      const updatedWidgets = widgets.map(widget => {
        // Find matching saved widget by name and original ID
        const savedWidget = savedWidgets.find(saved =>
          (saved.original_id === widget.id || saved.temp_id === widget.id) ||
          (saved.name === widget.name && !widget.id.toString().startsWith('preview-'))
        );

        if (savedWidget) {
          return { ...widget, id: savedWidget.id, isPreview: false };
        }
        return widget;
      });

      setWidgets(updatedWidgets);
      setOriginalWidgets(updatedWidgets); // Update original widgets to reflect current state

      // Show success message
      const deletedCount = deletedWidgets.length;
      const savedCount = savedWidgets.length;
      const message = deletedCount > 0
        ? `Successfully saved ${savedCount} widget(s) and deleted ${deletedCount} widget(s) from dashboard!`
        : `Successfully saved ${savedCount} widget(s) to dashboard!`;

      console.log(message);

      // Navigate back to dashboard list page after successful save
      navigate('/dashboards');

    } catch (err) {
      console.error('Error saving dashboard:', err);
      setError('Error saving dashboard. Please try again.');
      setSaving(false);
    }
  };


  const getChartTypeLabel = (chartType) => {
    switch (chartType) {
      case 'bar':
        return 'Bar Chart';
      case 'line':
        return 'Line Chart';
      case 'pie':
        return 'Pie Chart';
      case 'area':
        return 'Area Chart';
      case 'table':
        return 'Table';
      default:
        return 'Chart';
    }
  };

  const transformChartForDisplay = (widget) => {
    console.log('DashboardCanvas - Transforming widget for chart display:', widget);
    if (!widget) return null;

    // Extract chart configuration from widget - use chart_config from the response data
    const chartConfig = widget.chart_config || widget.selectedChart || {};

    // Create chart object by merging widget properties with chart_config data
    const chart = {
      // Basic chart properties from widget
      id: widget.chart_id || widget.id,
      title: widget.name,
      chart_type: chartConfig.chartType || widget.chart_type,

      // Chart configuration from chart_config
      ...chartConfig,

      // Widget-specific properties
      chartType: chartConfig.chartType,

      // Scatter chart specific fields
      scatterXAxis: chartConfig.scatterXAxis,
      scatterYAxis: chartConfig.scatterYAxis,
      scatterGroupField: chartConfig.scatterGroupField,
      scatterXAxisLabel: chartConfig.scatterXAxisLabel,
      scatterYAxisLabel: chartConfig.scatterYAxisLabel,

      // Other chart types
      xAxisField: chartConfig.xAxisField,
      yAxisField: chartConfig.yAxisField,
      yAxisFields: chartConfig.yAxisFields,
      groupField: chartConfig.groupField,
      pieValueField: chartConfig.pieValueField,
      pieSliceField: chartConfig.pieSliceField,

      // Display properties
      legendPosition: chartConfig.legendPosition || 'top',
      showTooltips: chartConfig.showTooltips !== undefined ? chartConfig.showTooltips : true,
      showLabels: chartConfig.showLabels !== undefined ? chartConfig.showLabels : true,
      labelColor: chartConfig.labelColor,
      xAxisLabel: chartConfig.xAxisLabel,
      yAxisLabel: chartConfig.yAxisLabel,
      fieldColors: chartConfig.fieldColors,
      legendColors: chartConfig.legendColors,
      showValues: chartConfig.showValues,
      showPercentages: chartConfig.showPercentages,
      showTotal: chartConfig.showTotal,
      valueFieldType: chartConfig.valueFieldType
    };

    console.log('DashboardCanvas - Transformed chart from widget:', chart);
    return chart;
  };

  const runUserDisplayName = runUserDisplayLabel
    ? runUserDisplayLabel.split('|')[0].trim()
    : '';


  if (loading) {
    return (
      <div className={`dashboard-canvas-main ${embedded ? 'dashboard-canvas-embedded' : ''}`}>
        <div className="canvas-loading">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard-canvas-main ${embedded ? 'dashboard-canvas-embedded' : ''}`}>
      {/* Header */}
      {showHeader && (
        <div className="canvas-header">
          <div className="canvas-header-content">
            <div className="canvas-header-left">
              {!isRunMode && !embedded && (
                <button
                  type="button"
                  className="canvas-back-btn"
                  onClick={() => navigate('/dashboards')}
                >
                  <FaArrowLeft aria-hidden />
                  Back to dashboards
                </button>
              )}
              <div className="canvas-title">
              <h1>
                {loading && !dashboard ? (
                  <span className="loading-text">Loading...</span>
                ) : (
                  dashboard?.name || 'Dashboard Canvas'
                )}
              </h1>
              <p className="canvas-description">
                {loading && !dashboard ? (
                  <span className="loading-text">Loading dashboard details...</span>
                ) : (
                  <>
                    {dashboard?.label && (
                      <span className="dashboard-label">{dashboard.label}</span>
                    )}
                    {dashboard?.description && (
                      <span className="dashboard-description">{dashboard.description}</span>
                    )}
                    {!dashboard?.label && !dashboard?.description && (
                      <span>{isRunMode ? 'Dashboard View' : 'Manage and view your dashboard widgets'}</span>
                    )}
                  </>
                )}
              </p>
              </div>
            </div>
            {!isRunMode && (
              <div className="canvas-actions">
                <button
                  className="canvas-action-btn canvas-save-btn"
                  onClick={handleSaveDashboard}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Dashboard'}
                </button>
                <button
                  className="canvas-action-btn canvas-add-btn"
                  onClick={() => setShowSidebar(true)}
                >
                  <FaPlus />
                  Add Widget
                </button>
                {/* <button
                  className="canvas-action-btn canvas-settings-btn"
                  title="Dashboard Settings"
                >
                  <FaCog />
                </button> */}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="canvas-error">
          <p>{error}</p>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Canvas Grid */}
      <div className="canvas-grid-container">
        {widgets.length === 0 ? (
          <div className="canvas-empty-state">
            <div className="empty-state-content">
              <div className="empty-icon">
                <FaPlus />
              </div>
              <h3>No widgets yet</h3>
              <p>{isRunMode ? 'This dashboard has no widgets to display' : 'Start building your dashboard by adding widgets'}</p>
              {!isRunMode && (
                <button
                  className="canvas-add-btn"
                  onClick={() => setShowSidebar(true)}
                >
                  <FaPlus />
                  Add Your First Widget
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className={`canvas-grid ${isRunMode ? 'run-mode' : ''}`} ref={canvasRef}>
            {widgets
              .sort((a, b) => {
                // Handle widgets without sort_order (put them at the end)
                const aOrder = a.sort_order || 999999;
                const bOrder = b.sort_order || 999999;

                // Sort by sort_order (ascending)
                return aOrder - bOrder;
              })
              .map((widget, index) => {
                const normalizedChartType = (widget.chart_type || widget.type || '').toLowerCase();
                const isDashboardWidget = normalizedChartType === 'dashboard';
                const isReportWidget = normalizedChartType === 'report';
                const supportsRefresh = !(isDashboardWidget || isReportWidget);
                const parentId = widget.parent_id || widget.report_id || null;
                const columnSpan = widget.column_span || 6;
                const rowSpan = widget.row_span || 4;
                console.log(`DashboardCanvas - Rendering widget ${widget.id} (position ${index + 1}) with sort_order: ${widget.sort_order || 'none'}, column_span: ${columnSpan}, row_span: ${rowSpan}`);
                console.log(`DashboardCanvas - Widget ${widget.id} grid styles:`, {
                  gridColumn: `span ${columnSpan}`,
                  gridRow: `span ${rowSpan}`,
                  gridColumnStart: widget.x || 'auto',
                  gridRowStart: widget.y || 'auto'
                });
                console.log(`DashboardCanvas - Widget ${widget.id} raw data:`, widget);

                return (
                  <div
                    key={widget.id}
                    className={`canvas-widget widget-${normalizedChartType || 'chart'} ${isResizing && draggedWidget?.id === widget.id ? 'resizing' : ''}`}
                    style={{
                      gridColumn: `span ${columnSpan}`,
                      gridRow: `span ${rowSpan}`,
                      gridColumnStart: widget.x || 'auto',
                      gridRowStart: widget.y || 'auto',
                      '--column-span': columnSpan,
                      '--row-span': rowSpan
                    }}
                    data-column-span={columnSpan}
                    data-row-span={rowSpan}

                  >
                    {/* Widget Header */}
                    <div className="widget-header">
                      <div className="widget-title-section">
                        <div className="widget-title-info">
                          <h3 className="widget-title">
                            {widget.name}
                            {widget.isPreview && (
                              <span className="preview-badge">Preview</span>
                            )}

                          </h3>
                          {runUserDisplayName && (
                            <div className="widget-view-as-info">
                              <span className="widget-view-as-label">View as:</span>
                              <span className="widget-view-as-value" title={runUserDisplayName}>
                                {runUserDisplayName}
                              </span>
                            </div>
                          )}
                          {widget.refresh_datetime && (
                            <div className="widget-refresh-info" style={{
                              fontSize: '12px',
                              color: '#666',
                              marginTop: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span className="refresh-label" style={{ fontWeight: '500' }}>Last refreshed:</span>
                              <span className="refresh-datetime" style={{
                                fontFamily: 'monospace',
                                backgroundColor: '#f0f0f0',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                fontSize: '11px'
                              }}>
                                {formatDateTime(widget.refresh_datetime)}
                              </span>
                            </div>
                          )}

                        </div>
                      </div>
                      <div className="widget-actions">
                        <button
                          className="widget-action-btn widget-expand-btn"
                          onClick={() => handleViewWidget(widget)}
                          title="View Widget in Full Screen"
                        >
                          <FaExpand />
                        </button>
                        {supportsRefresh && (
                          <button
                            className="widget-action-btn widget-refresh-btn"
                            onClick={() => handleRefreshWidget(widget)}
                            title="Refresh Widget Data"
                            disabled={loadingWidgets[widget.id]}
                          >
                            <FaSync className={loadingWidgets[widget.id] ? 'spinning' : ''} />
                          </button>
                        )}
                        {!isRunMode && !embedded && (
                          <>
                            <button
                              className="widget-action-btn widget-edit-btn"
                              onClick={() => handleEditWidget(widget)}
                              title="Edit Widget"
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="widget-action-btn widget-delete-btn"
                              onClick={() => handleRemoveWidget(widget.id)}
                              title="Remove Widget"
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Widget Content */}
                    <div className="widget-content">
                      <div className="widget-chart-container">
                        {isDashboardWidget && parentId ? (
                          <div className="widget-dashboard-viewer">
                            <DashboardCanvas
                              isRunMode={true}
                              dashboardId={parentId}
                              showHeader={widget.show_header !== undefined ? widget.show_header : true}
                              embedded={true}
                            />
                          </div>
                        ) : isReportWidget && parentId ? (
                          <div className="widget-report-viewer">
                            <ReportViewer
                              embed={true}
                              reportId={parentId}
                              onError={(message) => {
                                console.error('DashboardCanvas - ReportViewer error:', message);
                              }}
                            />
                          </div>
                        ) : loadingWidgets[widget.id] ? (
                          <div className="widget-loading">
                            <div className="loading-spinner"></div>
                            <p>Loading chart...</p>
                          </div>
                        ) : widgetChartData[widget.id] ? (
                          <div className="widget-chart">
                            {console.log('DashboardCanvas - ChartDisplay props for widget:', widget.id, {
                              selectedFields: widgetChartData[widget.id].selectedFields,
                              summarizedFields: widgetChartData[widget.id].summarizedFields,
                              selectedGroups: widgetChartData[widget.id].selectedGroups,
                              summarizedData: widgetChartData[widget.id].summarizedData,
                              previewData: widgetChartData[widget.id].previewData
                            })}
                            <ChartDisplay
                              chart={transformChartForDisplay(widget)}
                              selectedFields={widgetChartData[widget.id].selectedFields || []}
                              summarizedFields={widgetChartData[widget.id].summarizedFields || {}}
                              selectedGroups={widgetChartData[widget.id].selectedGroups || []}
                              summarizedData={widgetChartData[widget.id].summarizedData}
                              previewData={widgetChartData[widget.id].previewData}
                              isPreview={true}
                              displayAuditInfo={false}
                            />
                          </div>
                        ) : (
                          <div className="widget-chart">
                            <div className="chart-container">
                              {widget.name ? (
                                <div className="chart-title">{widget.name}</div>
                              ) : null}
                              <div
                                className="chart-placeholder"
                                style={{ textAlign: 'center', color: '#6c757d', padding: '1rem' }}
                              >
                                No data available
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Widget Footer */}
                    <div className="widget-footer">
                      <div className="widget-meta">
                        {isDashboardWidget && parentId && (
                          <span
                            className="widget-dashboard widget-dashboard-link"
                            onClick={() => window.open(`/dashboard/${parentId}/run`, '_blank')}
                            title="Click to open dashboard"
                          >
                            Dashboard: {widget.dashboard_label || widget.name || 'Unknown'}
                          </span>
                        )}
                        {!isDashboardWidget && parentId && (isReportWidget || widget.report_label || widget.report_id) && (
                          <span
                            className="widget-report widget-report-link"
                            onClick={() => window.open(`/viewreport/${parentId}?source=reports`, '_blank')}
                            title="Click to open report"
                          >
                            Report: {widget.report_label || widget.name || 'Unknown'}
                          </span>
                        )}
                      </div>
                      {!isRunMode && (
                        <div className="widget-resize-handles">
                          <div
                            className="resize-handle resize-se"
                            data-handle="bottom-right"
                            onMouseDown={(e) => {
                              // Get the handle from the current target (the resize handle div)
                              const handle = e.currentTarget.dataset.handle || 'bottom-right';
                              console.log('DashboardCanvas - Resize handle clicked:', handle);
                              handleMouseDown(e, widget, 'resize');
                            }}
                            onClick={(e) => {
                              console.log('DashboardCanvas - Resize handle onClick:', e.target.dataset.handle);
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            title="Resize both width and height"
                          >
                            <FaExpand />
                          </div>
                          <div
                            className="resize-handle resize-s"
                            data-handle="bottom"
                            onMouseDown={(e) => {
                              // Get the handle from the current target (the resize handle div)
                              const handle = e.currentTarget.dataset.handle || 'bottom-right';
                              console.log('DashboardCanvas - Resize handle clicked:', handle);
                              handleMouseDown(e, widget, 'resize');
                            }}
                            title="Resize height"
                          >
                            <FaArrowsAlt />
                          </div>
                          <div
                            className="resize-handle resize-e"
                            data-handle="right"
                            onMouseDown={(e) => {
                              // Get the handle from the current target (the resize handle div)
                              const handle = e.currentTarget.dataset.handle || 'bottom-right';
                              console.log('DashboardCanvas - Resize handle clicked:', handle);
                              handleMouseDown(e, widget, 'resize');
                            }}
                            title="Resize width"
                          >
                            <FaArrowsAlt />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Widget Builder */}
      {!isRunMode && (
        <WidgetBuilder
          isOpen={showSidebar}
          onClose={() => {
            setShowSidebar(false);
            setEditingWidget(null);
          }}
          dashboardId={dashboardId}
          onAddWidget={handleAddWidget}
          onPreviewWidget={handlePreviewWidget}
          editingWidget={editingWidget}
        />
      )}

      {/* Widget View Modal */}
      {viewingWidget && (
        <div className="widget-view-modal-overlay" onClick={handleCloseViewModal}>
          <div className="widget-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="widget-view-modal-header">
              <h3>{viewingWidget.name}</h3>
              <button className="widget-view-modal-close" onClick={handleCloseViewModal}>
                ×
              </button>
            </div>
            <div className="widget-view-modal-body">
              {(() => {
                const viewingNormalizedType = (viewingWidget.type || viewingWidget.chart_type || '').toLowerCase();
                const viewingParentId = viewingWidget.parent_id || viewingWidget.report_id || null;

                if (viewingNormalizedType === 'dashboard' && viewingWidget.parent_id) {
                  return (
                    <DashboardCanvas
                      isRunMode={true}
                      dashboardId={viewingWidget.parent_id}
                      showHeader={viewingWidget.show_header !== undefined ? viewingWidget.show_header : true}
                      embedded={true}
                    />
                  );
                }

                if (viewingNormalizedType === 'report' && viewingParentId) {
                  return (
                    <ReportViewer
                      embed={true}
                      reportId={viewingParentId}
                      onError={(message) => {
                        console.error('DashboardCanvas - ReportViewer modal error:', message);
                      }}
                    />
                  );
                }

                if (widgetChartData[viewingWidget.id]) {
                  return (
                    <ChartDisplay
                      chart={transformChartForDisplay(viewingWidget)}
                      selectedFields={widgetChartData[viewingWidget.id].selectedFields || []}
                      summarizedFields={widgetChartData[viewingWidget.id].summarizedFields || {}}
                      selectedGroups={widgetChartData[viewingWidget.id].selectedGroups || []}
                      summarizedData={widgetChartData[viewingWidget.id].summarizedData}
                      previewData={widgetChartData[viewingWidget.id].previewData}
                      isPreview={true}
                      displayAuditInfo={true}
                    />
                  );
                }

                return (
                  <div className="widget-view-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading widget data...</p>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setWidgetToDelete(null);
        }}
        onConfirm={confirmDeleteWidget}
        title="Confirm Delete"
        message="Are you sure you want to delete this widget from the dashboard?"
        itemName={widgetToDelete?.name}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default DashboardCanvas;
